import InfluxDBService from "@/services/InfluxDBService";
import { getRawData } from "@/services/data";

export default {
    namespaced: true,

    state: {
        data: null,
        gyroData: null,
        loading: false,
        error: null,
    },

    getters: {
        useDemoData: (state) => {
            return state.auth.email.includes("demo@demo");
        },
        handleData: (state) => {
            if (!state.data || !state.data.length) return null;

            const data = state.data.slice(1);
            let headers = state.data[0];
            let items = [];
            for (let i in data) {
                let item = {};
                for (let j in headers) {
                    if (
                        j > 0 &&
                        headers[j] !== "result" &&
                        headers[j] !== "table" &&
                        headers[j] !== data[i]
                    ) {
                        item[headers[j].replace("\r", "")] = data[i][j];
                    }
                }
                if (item._time !== "_time") {
                    items.push(item);
                } else {
                    headers = data[i];
                }
            }
            return items;
        },
        normalizedData: (state) => {
            const data = state.gyroData;

            console.log("normalizedData getter called with gyroData:", data);

            if (!data || data.length === 0) {
                console.log("No data available in gyroData state");
                return [];
            }

            // Case 1: Data is already normalized (from demo data)
            if (Array.isArray(data) && data[0] && data[0].timestamp &&
                (data[0].accel_x !== undefined || data[0].gyro_x !== undefined)) {
                console.log("Data is already in normalized format, returning as is");
                return data.map(d => ({
                    deviceId: d.deviceId || d.device_id || "unknown",
                    timestamp: d.timestamp,
                    accel_x: parseFloat(d.accel_x) || 0,
                    accel_y: parseFloat(d.accel_y) || 0,
                    accel_z: parseFloat(d.accel_z) || 0,
                    gyro_x: parseFloat(d.gyro_x) || 0,
                    gyro_y: parseFloat(d.gyro_y) || 0,
                    gyro_z: parseFloat(d.gyro_z) || 0,
                    temperature: parseFloat(d.temperature) || 72,
                    percentage: parseFloat(d.percentage) || 82
                }));
            }

            // Case 2: InfluxDB data format
            try {
                if (data[0] && data[0].result) {
                    const tableResult = data[0].result[0] || { table: null };

                    if (!tableResult.header || !Array.isArray(tableResult.header) ||
                        !tableResult.table || !Array.isArray(tableResult.table)) {
                        console.log("Invalid InfluxDB result structure");
                        return [];
                    }

                    const headers = tableResult.header.map(h => h.label);
                    console.log("Processing InfluxDB data with headers:", headers);

                    const normalized = [];

                    tableResult.table.forEach((row) => {
                        const record = {
                            deviceId: null,
                            timestamp: null,
                            temperature: 0,
                            percentage: 0,
                            accel_x: 0,
                            accel_y: 0,
                            accel_z: 0,
                            gyro_x: 0,
                            gyro_y: 0,
                            gyro_z: 0,
                        };

                        // First pass: extract timestamp and direct columns
                        row.forEach((value, index) => {
                            const header = headers[index];

                            if (header === "_time") {
                                record.timestamp = value;
                            } else if (header === "device_id" || header === "deviceId") {
                                record.deviceId = value;
                            } else if (header === "accel_x" || header === "accel_y" ||
                                header === "accel_z" || header === "gyro_x" ||
                                header === "gyro_y" || header === "gyro_z") {
                                const cleanHeader = header.replace(/[\r\n\s]/g, "");
                                record[cleanHeader] = parseFloat(value) || 0;
                            } else if (header === "temperature" || header === "temp") {
                                record.temperature = parseFloat(value) || 0;
                            } else if (header === "percentage" || header === "batt") {
                                record.percentage = parseFloat(value) || 0;
                            }
                        });

                        // Second pass: handle InfluxDB _field/_value pairs
                        const fieldIndex = headers.indexOf("_field");
                        const valueIndex = headers.indexOf("_value");
                        const deviceIndex = headers.indexOf("device_id");

                        if (fieldIndex !== -1 && valueIndex !== -1) {
                            const fieldName = row[fieldIndex];
                            const fieldValue = parseFloat(row[valueIndex]) || 0;

                            // Get device ID if available
                            if (deviceIndex !== -1 && !record.deviceId) {
                                record.deviceId = row[deviceIndex];
                            }

                            switch (fieldName) {
                                case "accel_x":
                                    record.accel_x = fieldValue;
                                    break;
                                case "accel_y":
                                    record.accel_y = fieldValue;
                                    break;
                                case "accel_z":
                                    record.accel_z = fieldValue;
                                    break;
                                case "gyro_x":
                                    record.gyro_x = fieldValue;
                                    break;
                                case "gyro_y":
                                    record.gyro_y = fieldValue;
                                    break;
                                case "gyro_z":
                                    record.gyro_z = fieldValue;
                                    break;
                                case "temp":
                                case "temperature":
                                    record.temperature = fieldValue;
                                    break;
                                case "batt":
                                case "percentage":
                                    record.percentage = fieldValue;
                                    break;
                                case "device_id":
                                case "deviceId":
                                    record.deviceId = row[valueIndex];
                                    break;
                            }
                        }

                        // If deviceId is still null, try to find it in the tag field
                        if (!record.deviceId) {
                            const tagIndex = headers.indexOf("tags");
                            if (tagIndex !== -1) {
                                try {
                                    const tags = JSON.parse(row[tagIndex]);
                                    if (tags && tags.device_id) {
                                        record.deviceId = tags.device_id;
                                    }
                                } catch (e) {
                                    // If we can't parse tags, default to unknown
                                    record.deviceId = "unknown";
                                }
                            } else {
                                record.deviceId = "unknown";
                            }
                        }

                        // Only add records that have a timestamp
                        if (record.timestamp) {
                            console.log("Adding normalized record:", record);
                            normalized.push(record);
                        }
                    });

                    // Use default values for missing data
                    normalized.forEach(record => {
                        if (record.temperature === 0) record.temperature = 72;
                        if (record.percentage === 0) record.percentage = 82;
                        if (!record.deviceId) record.deviceId = "unknown";
                    });

                    console.log(`Normalized ${normalized.length} records from InfluxDB data`);
                    return normalized;
                } else {
                    console.log("Unknown data format:", data);
                    return [];
                } 
            } catch (error) {
                console.error("Error normalizing data:", error);
                return [];
            }
        },
    },

    mutations: {
        SET_DATA(state, data) {
            state.data = data;
        },
        SET_LOADING(state, loading) {
            state.loading = loading;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        setGyroData(state, data) {
            state.gyroData = data;
            console.log("gyroData state updated:", data ? "Data present" : "No data");
        },
    },

    actions: {
        async fetchDataByRange({ commit }, { deviceId, start, end, group, isDemo }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            console.log(`Fetching data for device: ${deviceId}, isDemo: ${isDemo}`);
            try {
                let result;

                if (isDemo) {
                    // Get mock data for demo users
                    const demoData = await getRawData(deviceId, start, end, group);
                  

                    // For demo data, we just pass it through as-is
                    result = demoData;
                } else {

                    // Create timeRange object for InfluxDB query
                    const timeRange = {
                        start: start,
                        stop: end,
                    };

                    console.log("Device ID for query:", deviceId);
                   
                    // Call the InfluxDBService queryGyroData method
                    result = await InfluxDBService.queryGyroData(
                        deviceId,
                        timeRange,
                        group || "5s",
                        false // Never pass isDemo=true to InfluxDB
                    );
                 

                    // Check if we got a valid result
                    if (!result || !Array.isArray(result) || result.length === 0) {
                        console.log("No data returned from InfluxDB or invalid format");
                    }
                }

                // Commit data to store (empty array if nothing returned)
                commit("setGyroData", result || []);
            } catch (error) {
                console.error("Error fetching data:", error);
                commit("SET_ERROR", error.message);
                // Set empty data to prevent UI errors
                commit("setGyroData", []);
            } finally {
                commit("SET_LOADING", false);
            }
        },

        // Keep legacy method for backward compatibility
        async fetchGyroData(context, params) {
            console.log(params, "<--- params")
            // Call directly to avoid 'this' context issues
            return context.dispatch('fetchDataByRange', params);
        },
    },
};